import { useAuth0 } from '@auth0/auth0-react';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Redirect, Route, Switch, useHistory } from 'react-router-dom';
import HashedRouter, { UrlBuilder } from './common/HashedRouter';
import ErrorBoundary from './ErrorBoundary';
import { InventoryPage } from './inventory';
import { DocumentTitle, DocumentTitleProvider } from './layout/navigation/DocumentTitle';
import RoutedModal from './layout/RoutedModal';
import ProfilePage from './profile/ProfilePage';
import Modal from './layout/Modal';
import TermsAndConditions from './termsAndConditions/TermsAndConditions';
import WithAcceptation from './termsAndConditions/WithAcceptation';
import { TransactionPage } from './transaction';
import AppointmentPage from './appointment/AppointmentPage';
import MaintenancePage from './maintenance/MaintenancePage';
import axios from 'axios';
import { CompaniesContextProvider } from './layout/navigation/CompaniesContextProvider';
import viewAsCompanyRoles from './common/helpers/viewAsCompanyRoles';
import ExcelReportDashboard from './excelReports/components/ExcelReportsPage';
import { ROUTE_DEFINITIONS } from './routes';

export const ROUTES = {
  transaction: {
    ...ROUTE_DEFINITIONS.transaction,
    component: TransactionPage,
  },
  inventory: {
    ...ROUTE_DEFINITIONS.inventory,
    component: InventoryPage,
  },
  reports: {
    ...ROUTE_DEFINITIONS.reports,
    component: ExcelReportDashboard,
  },
  appointments: {
    ...ROUTE_DEFINITIONS.appointments,
    component: AppointmentPage,
  },
  profile: {
    ...ROUTE_DEFINITIONS.profile,
    component: ProfilePage,
    componentProps: {
      isWorker: false,
    },
  },
  maintenance: {
    ...ROUTE_DEFINITIONS.maintenance,
    component: MaintenancePage,
    componentProps: {
      mainMessageKey: 'clientPortalMainMessage',
      secondaryMessageKey: 'clientPortalSecondaryMessage',
      module: 'client',
    },
  },

  // ANY ROUTE AFTER THIS ONE WILL NOT BE REACHED
};

const MyRoute = ({ Component, title, ...rest }) => {
  const history = useHistory();

  useEffect(() => {
    if (history.location.pathname === '/maintenance') return;

    axios.get(`/api/v1/maintenance/client`).catch((err) => {
      if (err.response.status === 503) {
        history.push({ state: { secondaryMessage: err.response.data }, pathname: '/maintenance' });
      }
    });
  }, [history]);

  const { t } = useTranslation();

  return (
    <>
      <DocumentTitle title={t(title)} />
      <ErrorBoundary>
        <Component {...rest} />
      </ErrorBoundary>
    </>
  );
};

MyRoute.propTypes = {
  Component: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
};

const termsAndConditionsAcceptedDate = 'http://qsl.com/termsAndConditionsAcceptedDate';

const Router = ({ groups }) => {
  const { user, logout } = useAuth0();
  const [hasAcceptedTermsAndConditions, setHasAcceptedTermsAndConditions] = useState(user && user[termsAndConditionsAcceptedDate]);
  const handleAccept = () => setHasAcceptedTermsAndConditions(true);
  const handleRefuse = () => logout({ returnTo: window.location.origin });

  const canViewAsCompany = groups.some((g) => viewAsCompanyRoles.includes(g));
  const canAccessApplication = hasAcceptedTermsAndConditions || canViewAsCompany;

  return (
    <DocumentTitleProvider>
      <CompaniesContextProvider canViewAsCompany={canViewAsCompany}>
        <BrowserRouter>
          {canAccessApplication && (
            <Switch>
              {Object.values(ROUTES).map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  render={(routeProps) => <MyRoute {...routeProps} {...route.componentProps} title={route.i18nKey} Component={route.component} />}
                />
              ))}
              <Redirect from="/" to={ROUTES.inventory.path} />
            </Switch>
          )}
          <HashedRouter
            routes={[
              {
                path: UrlBuilder.buildPattern('terms-and-conditions'),
                // eslint-disable-next-line react/prop-types
                component: () => (
                  <RoutedModal fullWidth maxWidth="lg">
                    <TermsAndConditions />
                  </RoutedModal>
                ),
              },
            ]}
          />
          <Modal open={!canAccessApplication}>
            <WithAcceptation onRefuse={handleRefuse} onAccept={handleAccept}>
              <TermsAndConditions />
            </WithAcceptation>
          </Modal>
        </BrowserRouter>
      </CompaniesContextProvider>
    </DocumentTitleProvider>
  );
};

Router.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default React.memo(Router);
